import {Eq, eqString, getStructEq} from 'fp-ts/lib/Eq';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type TaxIdNumberValue = string;

const tinRegex = /^\d{9}/;
const formatTIN = (tin: string) =>
  pipe(
    fromNullable(tin.match(/\d/g)),
    foldOption(
      () => '',
      tin => tin.join('')
    )
  );

export class UnvalidatedTaxIdentificationNumber {
  public readonly key = 'UnvalidatedTaxIdentificationNumber';
  public readonly value: string;

  constructor(value: string) {
    this.value = formatTIN(value);
  }
}

export class InvalidTaxIdentificationNumber {
  public static create(value: TaxIdNumberValue) {
    return value === ''
      ? new UnvalidatedTaxIdentificationNumber('')
      : new InvalidTaxIdentificationNumber(formatTIN(value));
  }
  public readonly error = 'Please enter a valid Tax ID number.';
  public readonly key = 'InvalidTaxIdentificationNumber';

  private constructor(public readonly value: TaxIdNumberValue) {}
}

export class EncryptedTaxIdentificationNumber {
  public readonly key = 'EncryptedTaxIdentificationNumber';

  constructor(public readonly value: string) {}
}

export class ExistingTaxIdentificationNumber {
  public readonly key = 'ExistingTaxIdentificationNumber';

  constructor(public readonly maskedIdentifier: string, public readonly value: string) {}
}

export class ValidTaxIdentificationNumber {
  public static create(tin: TaxIdNumberValue, tins?: TaxIdentificationNumber[]) {
    const formattedNumber = formatTIN(tin);

    return tinRegex.test(formattedNumber)
      ? tins && tins.find(taxIdentifier => taxIdentifier.value === formattedNumber)
        ? DuplicateTaxIdentificationNumber.create(formattedNumber)
        : new ValidTaxIdentificationNumber(formattedNumber)
      : InvalidTaxIdentificationNumber.create(formattedNumber);
  }

  public readonly key = 'ValidTaxIdentificationNumber';

  private constructor(public readonly value: string) {}
}

export class DuplicateTaxIdentificationNumber {
  public static create(tin: TaxIdNumberValue) {
    const formattedNumber = formatTIN(tin);

    return new DuplicateTaxIdentificationNumber(formattedNumber);
  }
  public readonly error = 'Tax ID number already exist.';
  public readonly key = 'DuplicateTaxIdentificationNumber';

  private constructor(public readonly value: string) {}
}

export type TaxIdentificationNumber =
  | EncryptedTaxIdentificationNumber
  | InvalidTaxIdentificationNumber
  | ValidTaxIdentificationNumber
  | ExistingTaxIdentificationNumber
  | UnvalidatedTaxIdentificationNumber
  | DuplicateTaxIdentificationNumber;

type TaxIdentificationNumberOptions<TReturn> = ToMatchOptions<TaxIdentificationNumber, 'key', TReturn>;

export const matchTaxIdentificationNumber = <TReturn>(
  taxIdNumber: TaxIdentificationNumber,
  options: TaxIdentificationNumberOptions<TReturn>
) => {
  switch (taxIdNumber.key) {
    case 'EncryptedTaxIdentificationNumber':
      return options.EncryptedTaxIdentificationNumber(taxIdNumber);
    case 'InvalidTaxIdentificationNumber':
      return options.InvalidTaxIdentificationNumber(taxIdNumber);
    case 'ExistingTaxIdentificationNumber':
      return options.ExistingTaxIdentificationNumber(taxIdNumber);
    case 'UnvalidatedTaxIdentificationNumber':
      return options.UnvalidatedTaxIdentificationNumber(taxIdNumber);
    case 'ValidTaxIdentificationNumber':
      return options.ValidTaxIdentificationNumber(taxIdNumber);
    case 'DuplicateTaxIdentificationNumber':
      return options.DuplicateTaxIdentificationNumber(taxIdNumber);
  }
};

export const eqTaxIdNumber: Eq<TaxIdentificationNumber> = getStructEq({
  value: eqString,
});
